import {
  MUSIC_COLLECTION,
  SFX_COLLECTION,
  SFX_COLLECTION_WITH_ENGAGEMENT,
  STOCK_VIDEO_ENGAGEMENT_COLLECTION,
} from './collections';
import { VIDEO_ENGAGEMENT_SORT } from './stock-video-engagement-join';
import { TRACK_ENGAGEMENT_SORT } from './track-engagement-join';

export type SortOption = 'relevancy' | 'latest' | 'undiscovered';

export const VALID_SORT_OPTIONS: readonly SortOption[] = [
  'relevancy',
  'latest',
  'undiscovered',
] as const;

export const isValidSort = (sort: unknown): sort is SortOption =>
  typeof sort === 'string' &&
  (VALID_SORT_OPTIONS as readonly string[]).includes(sort);

interface CollectionSortConfig {
  latestSort: string;
  metricField: {
    premium: string;
    free: string;
  };
  relevanceField: {
    premium: string;
    free: string;
  };
  relevancySort?: (relevanceField: string, metricField: string) => string;
  supportsFeatured?: boolean;
  // false when the collection has a single sort score with no `_v{n}` suffix.
  versionedScore?: boolean;
}

const COLLECTION_SORT_CONFIGS = new Map<string, CollectionSortConfig>([
  [
    MUSIC_COLLECTION,
    {
      relevanceField: {
        premium: 'premium_sort_score',
        free: 'free_sort_score',
      },
      metricField: {
        premium: 'premium_decay_plays',
        free: 'free_decay_plays',
      },
      latestSort: 'set_live:desc',
      versionedScore: false,
    },
  ],
  [
    SFX_COLLECTION_WITH_ENGAGEMENT,
    {
      relevanceField: {
        premium: 'premium_sort_score',
        free: 'free_sort_score',
      },
      metricField: {
        premium: 'premium_decay_plays',
        free: 'free_decay_plays',
      },
      latestSort: 'set_live:desc',
    },
  ],
  [
    SFX_COLLECTION,
    {
      relevanceField: {
        premium: 'premium_sort_score',
        free: 'free_sort_score',
      },
      metricField: {
        premium: 'premium_decay_plays',
        free: 'free_decay_plays',
      },
      latestSort: 'set_live:desc',
    },
  ],
]);

const MOTION_GRAPHICS_CONFIG: CollectionSortConfig = {
  relevanceField: {
    premium: 'relevance_premium',
    free: 'relevance_free',
  },
  metricField: {
    premium: 'premium_download_count',
    free: 'free_download_count',
  },
  latestSort: 'set_live:desc',
  relevancySort: (relevanceField: string, metricField: string) =>
    `${relevanceField}:desc, ${metricField}:desc`,
  supportsFeatured: true,
};

const STOCK_VIDEO_CONFIG: Record<SortOption, string> = {
  relevancy: VIDEO_ENGAGEMENT_SORT,
  latest: 'set_live:desc',
  undiscovered: `$${STOCK_VIDEO_ENGAGEMENT_COLLECTION}(sort_score:asc)`,
};

export const getSortBy = (
  sortOption: SortOption,
  isPremium: boolean,
  featuredFirst = false,
  collection?: string,
  sortScoreVersion: 'v1' | 'v2' | 'v3' | 'v4' | 'v5' = 'v1',
  hasSearchTerms = false,
  isAlternativeFirst = false,
  // Music category/collection pages: rank by the joined per-page engagement
  // row. Only valid when the query's filter_by carries the matching join.
  joinPageEngagement = false
): string => {
  const config = collection
    ? (COLLECTION_SORT_CONFIGS.get(collection) ?? MOTION_GRAPHICS_CONFIG)
    : MOTION_GRAPHICS_CONFIG;

  const tier = isPremium ? 'premium' : 'free';
  const baseRelevanceField = config.relevanceField[tier];
  const metricField = config.metricField[tier];

  const relevanceField =
    config === MOTION_GRAPHICS_CONFIG || config.versionedScore === false
      ? baseRelevanceField
      : `${baseRelevanceField}_${sortScoreVersion}`;

  let sortString: string;

  switch (sortOption) {
    case 'latest':
      sortString = config.latestSort;
      break;

    case 'undiscovered':
      sortString = `${metricField}:asc`;
      break;

    case 'relevancy':
    default:
      if (hasSearchTerms) {
        sortString = '_text_match:desc';
      } else if (config.relevancySort) {
        const baseSort = config.relevancySort(relevanceField, metricField);
        sortString =
          config.supportsFeatured && featuredFirst
            ? `is_featured:desc, ${baseSort}`
            : baseSort;
      } else if (joinPageEngagement) {
        // Tracks with no engagement row for this page fall back to their
        // global score rather than an arbitrary order.
        sortString = `${TRACK_ENGAGEMENT_SORT}, ${relevanceField}:desc`;
      } else {
        sortString = `${relevanceField}:desc`;
      }
      break;
  }

  // Prepend alternative sort if enabled for music collections
  if (isAlternativeFirst && collection?.includes(MUSIC_COLLECTION)) {
    sortString = `is_alternative:desc, ${sortString}`;
  }

  return sortString;
};

export const getStockVideoSortBy = ({
  sortOption,
}: {
  sortOption: SortOption;
}): string => STOCK_VIDEO_CONFIG[sortOption];
